import type { Token } from '@/types'
import type { VTTCharacterData, TokenClassification } from '@/services/characterService'

/** A bank row: one campaign actor plus its token placements on the active
 *  map. `character` is null only for orphan tokens (a placed token with no
 *  character link), which we still surface so nothing on the map is hidden. */
export interface BankEntry {
  character: VTTCharacterData | null
  placements: Token[]
}

/**
 * Group the campaign actors (characters) into the sidebar's classification
 * buckets, attaching each actor's token placements on the active map.
 *
 * This is the heart of the map-independent bank: the list is driven by the
 * actor roster, not by the tokens currently on the map. An actor with zero
 * placements still appears (so it survives a token delete and can be
 * re-spawned); a placed token whose character isn't in the roster — or which
 * has no character at all — is surfaced as an orphan so map state is never
 * silently dropped.
 */
export function buildBankGroups(
  characters: VTTCharacterData[],
  tokens: Token[],
): Record<TokenClassification | 'unknown', BankEntry[]> {
  const groups: Record<TokenClassification | 'unknown', BankEntry[]> = {
    player: [],
    friendly: [],
    enemy: [],
    unknown: [],
  }

  const placementsByChar = new Map<string, Token[]>()
  const orphanTokens: Token[] = []
  for (const token of tokens) {
    if (token.characterId) {
      const arr = placementsByChar.get(token.characterId)
      if (arr) arr.push(token)
      else placementsByChar.set(token.characterId, [token])
    } else {
      orphanTokens.push(token)
    }
  }

  const seen = new Set<string>()
  for (const character of characters) {
    seen.add(character.id)
    const cls =
      character.tokenClassification && groups[character.tokenClassification]
        ? character.tokenClassification
        : 'unknown'
    groups[cls].push({
      character,
      placements: placementsByChar.get(character.id) ?? [],
    })
  }

  // Placements whose character isn't in the roster (and unlinked tokens) —
  // show them so nothing on the map disappears from the panel.
  for (const [charId, toks] of placementsByChar) {
    if (!seen.has(charId)) {
      for (const token of toks) groups.unknown.push({ character: null, placements: [token] })
    }
  }
  for (const token of orphanTokens) {
    groups.unknown.push({ character: null, placements: [token] })
  }

  return groups
}
